import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table/index";
import Badge from "../ui/badge/Badge";
import { FilterModal } from "../ui/modal/FilterModal";
import { useState, useEffect, useRef } from "react";
import Pagination from "../ui/Pagination/Pagination";
import PageMeta from "../common/PageMeta";
import PageBreadcrumb from "../common/PageBreadCrumb";
import OrderDetailsModal from "../ui/modal/OrderDetailsModal";
import { useModal } from "../../hooks/useModal";
import { Order } from "./Order";
import Spinner from "../ui/Spinner/Spinner";
import { FiPackage } from "react-icons/fi";
import { format } from "date-fns";
import { useOrdersStore } from "../../stores/useOrderStore";
import { RefreshIcon } from "../../icons";
import { useLocation } from 'react-router';
import { getOrderStatusBadgeColor } from "../../utils/helpers";
import { request } from "../../utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Currency {
  name: string;
  abbr: string;
  symbol: string;
  flag: string;
  country?: string;
  iso3?: string;
}


export default function AllOrders() {
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const location = useLocation();
  const { orders, filteredOrders, loading, hasInitialLoad, loadOrders, setFilteredOrders, refreshRecentOrders } = useOrdersStore();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const exportPanelRef = useRef<HTMLDivElement>(null);
  const fromDateRef = useRef<HTMLInputElement>(null);
const toDateRef = useRef<HTMLInputElement>(null);
  const [exportFormat, setExportFormat] = useState<"csv" | "pdf">("csv");

  // ---- Load logo once as base64 (needed for jsPDF)
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch("/images/logo/SecondaryLogoPNG.png"); // adjust path/extension to match your file
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error("Failed to load logo for PDF export", err);
      }
    };
    loadLogo();
  }, []);

  // ---- Close export panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportPanelRef.current &&
        !exportPanelRef.current.contains(event.target as Node)
      ) {
        setShowExportPanel(false);
      }
    };

    if (showExportPanel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportPanel]);

  // ---- CSV Export Helpers
  const escapeCSV = (value: any) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const convertOrdersToCSV = (data: Order[]) => {
    const headers = [
      "Order ID",
      "Payment Reference",
      "From Currency",
      "To Currency",
      "Outgoing Amount",
      "Incoming Amount",
      "Rate",
      "Fee",
      "Status",
      "Date",
    ];

    const rows = data.map((order) => [
      order._id,
      order.paymentReferenceCode,
      order.fromCurrency?.abbr,
      order.toCurrency?.abbr,
      `${order.fromCurrency?.symbol}${order.amount?.toLocaleString()}`,
      showValue(order),
      order.rate,
      order.fee,
      order.status,
      `="${formatDate(order.createdAt)}"`,
    ]);

    return [headers, ...rows]
      .map((row) => row.map(escapeCSV).join(","))
      .join("\n");
  };

  const downloadCSV = (data: Order[], filename: string) => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }
    const csvContent = convertOrdersToCSV(data);
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---- PDF Export Helper
  const downloadPDF = (data: Order[], filename: string, title: string) => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();

    const logoWidth = 28;
    const logoHeight = 12; // adjust to match your logo's actual aspect ratio

    const drawHeader = () => {
      if (logoBase64) {
        doc.addImage(
          logoBase64,
          "PNG",
          pageWidth - logoWidth - 14,
          10,
          logoWidth,
          logoHeight
        );
      }
      // doc.setFontSize(14);
      // doc.setTextColor(20);
      // doc.text(title, 14, 15);
      // doc.setFontSize(9);
      // doc.setTextColor(100);
      // doc.text(`Generated on ${format(new Date(), "dd/MM/yyyy HH:mm")}`, 14, 21);
      // doc.text(`Total records: ${data.length}`, 14, 26);

      doc.setFontSize(15);
doc.setFont("helvetica", "bold");
doc.setTextColor(20);
doc.text("Exchange Transaction History", 14, 15);

doc.setFontSize(10);
doc.setFont("helvetica", "normal");
doc.setTextColor(80);
doc.text(title, 14, 21);

doc.setFontSize(9);
doc.setTextColor(120);
doc.text(`Generated on ${format(new Date(), "dd/MM/yyyy HH:mm")}`, 14, 27);
doc.text(`Total records: ${data.length}`, 14, 32);
    };

    drawHeader();

    const headers = [
      [
        "Order ID",
        "Payment Ref",
        "Pair",
        "Outgoing",
        "Incoming",
        "Rate",
        "Fee",
        "Status",
        "Date",
      ],
    ];

    const rows = data.map((order) => [
      order._id,
      order.paymentReferenceCode,
      `${order.fromCurrency?.abbr}/${order.toCurrency?.abbr}`,
      `${order.fromCurrency?.abbr} ${order.amount?.toLocaleString()}`,
      // `${order.toCurrency?.abbr} ${order.amount?.toLocaleString()}`,
      showPDFValue(order),
      order.rate,
      order.fee,
      order.status,
      formatDate(order.createdAt),
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 38,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: {
        0: { cellWidth: 40 },
      },
      // Re-draw logo/header on every page (for multi-page exports)
      didDrawPage: (pageData) => {
        if (pageData.pageNumber > 1) {
          if (logoBase64) {
            doc.addImage(
              logoBase64,
              "PNG",
              pageWidth - logoWidth - 14,
              10,
              logoWidth,
              logoHeight
            );
          }
        }
      },
      margin: { top: 38 },
    });

    doc.save(filename);
  };

  // ---- Export handlers (CSV)
  const handleExportCurrentFiltered = () => {
    downloadCSV(
      filteredOrders,
      `exchanges-filtered-${format(new Date(), "yyyy-MM-dd")}.csv`
    );
  };

  const handleExportAll = () => {
    downloadCSV(orders, `exchanges-all-${format(new Date(), "yyyy-MM-dd")}.csv`);
  };

  const handleExportCurrent = () => {
  exportFormat === "csv" ? handleExportCurrentFiltered() : handleExportCurrentFilteredPDF();
};

const handleExportRange = () => {
  exportFormat === "csv" ? handleExportByDateRange() : handleExportByDateRangePDF();
};

const handleExportAllOrders = () => {
  exportFormat === "csv" ? handleExportAll() : handleExportAllPDF();
};

  const getDateRangeFiltered = () => {
    let result = [...orders];

    if (exportFromDate) {
      const fromDate = new Date(exportFromDate);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((order) => new Date(order.createdAt) >= fromDate);
    }

    if (exportToDate) {
      const toDate = new Date(exportToDate);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((order) => new Date(order.createdAt) <= toDate);
    }

    return result;
  };

  const handleExportByDateRange = () => {
    const result = getDateRangeFiltered();
    downloadCSV(
      result,
      `exchanges-${exportFromDate || "start"}-to-${exportToDate || "end"}.csv`
    );
  };

  // ---- Export handlers (PDF)
  const handleExportCurrentFilteredPDF = () => {
    downloadPDF(
      filteredOrders,
      `exchanges-filtered-${format(new Date(), "yyyy-MM-dd")}.pdf`,
      "Exchange Orders — Filtered View"
    );
  };

  const handleExportAllPDF = () => {
    downloadPDF(
      orders,
      `exchanges-all-${format(new Date(), "yyyy-MM-dd")}.pdf`,
      "Exchange Orders — All"
    );
  };

  const handleExportByDateRangePDF = () => {
    const result = getDateRangeFiltered();
    downloadPDF(
      result,
      `exchanges-${exportFromDate || "start"}-to-${exportToDate || "end"}.pdf`,
      `Exchange Orders — ${exportFromDate || "Start"} to ${exportToDate || "End"}`
    );
  };

  const close = () => {
    closeModal();
  };

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await request.get({ url: "/wallet/supported-currencies/all" });
        if (response.data && Array.isArray(response.data)) {
          setCurrencies(response.data.map((c: any) => ({
            name: c.name,
            abbr: c.abbr,
            symbol: c.symbol,
            flag: c.flag,
            country: c.country,
            iso3: c.iso3,
          })));
        }
      } catch (err) {
        console.error("Failed to fetch currencies", err);
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    let refreshTimeout: ReturnType<typeof setTimeout>;

    const smartRefresh = async () => {
      const isOnAllOrdersPage = location.pathname === '/exchanges';
      const isTabVisible = document.visibilityState === 'visible';

      if (!isOnAllOrdersPage || !isTabVisible) {
        return;
      }

      try {
        await refreshRecentOrders();
      } catch (error) {
        console.error("Refresh failed:", error);
      }

      refreshTimeout = setTimeout(smartRefresh, 30000);
    };

    const startRefreshCycle = () => {
      if (location.pathname === '/exchanges') {
        refreshTimeout = setTimeout(smartRefresh, 30000);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(refreshTimeout);
      }
      else if (document.visibilityState === 'visible' && location.pathname === '/exchanges') {
        startRefreshCycle();
      }
    };

    startRefreshCycle();

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(refreshTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasInitialLoad, loading, refreshRecentOrders, location.pathname]);

  const handleFilterApply = (filters: {
    status: string;
    fromDate: string;
    toDate: string;
    fromCurrency: string;
    toCurrency: string;
  }) => {
    let result = [...orders];

    if (filters.status && filters.status !== "all" && filters.status !== "") {
      result = result.filter(
        (order) => order.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.fromDate) {
      const fromDate = new Date(filters.fromDate);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((order) => new Date(order.createdAt) >= fromDate);
    }

    if (filters.toDate) {
      const toDate = new Date(filters.toDate);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((order) => new Date(order.createdAt) <= toDate);
    }

    if (filters.fromCurrency) {
      result = result.filter(
        (order) => order.fromCurrency.name === filters.fromCurrency
      );
    }

    if (filters.toCurrency) {
      result = result.filter(
        (order) => order.toCurrency.name === filters.toCurrency
      );
    }

    setFilteredOrders(result);
    setCurrentPage(1);
  };

  // ---- Open Single Order details page
  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    openModal();
  };

  // ---- Formatted Date
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // ---- Pagination data for filtered orders
  const paginatedData = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    Math.min(currentPage * itemsPerPage, filteredOrders.length)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  // Initial empty state (no orders at all)
  if (hasInitialLoad && orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <FiPackage className="w-12 h-12 mb-4" />
        <p className="text-lg">No recent exchange requests</p>
        <p className="text-sm">
          Your recent exchange requests will appear here
        </p>
      </div>
    );
  }

  const showValue = (order: Order) => {
    if (order.fee > 0) {
      const value = order.value - order.fee;
      return `${order.toCurrency.symbol} ${value.toLocaleString()}`
    } else {
      return `${order.toCurrency.symbol} ${order.value.toLocaleString()}`
    }
  }
  const showPDFValue = (order: Order) => {
    if (order.fee > 0) {
      const value = order.value - order.fee;
      return `${order.toCurrency.abbr} ${value.toLocaleString()}`
    } else {
      return `${order.toCurrency.abbr} ${order.value.toLocaleString()}`
    }
  }

  return (
    <>
      <PageMeta
        title="Order History"
        description="This is the page for a user order histories"
      />
      <PageBreadcrumb pageTitle="Exchanges" />
      <div className="overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Exchange Request
          </h3>
          <div className="flex items-center gap-1 relative" ref={exportPanelRef}>
            <button onClick={loadOrders} className="refresh-button">
              <RefreshIcon className="w-6 h-6" />
            </button>

            <button
              onClick={() => setShowExportPanel((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Export
            </button>

            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Filter
            </button>

            <FilterModal
              isOpen={showFilter}
              onClose={() => setShowFilter(false)}
              onApply={handleFilterApply}
              currencies={currencies}
            />

           {showExportPanel && (
  <div className="absolute right-0 top-12 z-20 w-72 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-800 dark:bg-gray-900">
    <div className="mb-4 flex items-center justify-between">
      <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">
        Export Options
      </h4>
      <div className="flex rounded-md border border-gray-300 p-0.5 dark:border-gray-700">
        {(["csv", "pdf"] as const).map((fmt) => (
          <button
            key={fmt}
            onClick={() => setExportFormat(fmt)}
            className={`px-2.5 py-1 text-xs font-medium rounded ${
              exportFormat === fmt
                ? "bg-brand-500 text-white"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {fmt.toUpperCase()}
          </button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <button
        onClick={handleExportCurrent}
        className="flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-white/[0.03]"
      >
        <span>Current View</span>
        <span className="text-xs text-gray-400">{filteredOrders.length}</span>
      </button>

      <button
        onClick={handleExportAllOrders}
        className="flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-white/[0.03]"
      >
        <span>All Orders</span>
        <span className="text-xs text-gray-400">{orders.length}</span>
      </button>

      <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
        <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
          By date range
        </p>
        <div className="mb-2 grid grid-cols-2 gap-2">
          <input
            ref={fromDateRef}
            type="date"
            value={exportFromDate}
            onChange={(e) => setExportFromDate(e.target.value)}
            onClick={() => fromDateRef.current?.showPicker?.()}
            className="w-full cursor-pointer rounded-md border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          />
          <input
            ref={toDateRef}
            type="date"
            value={exportToDate}
            onChange={(e) => setExportToDate(e.target.value)}
            onClick={() => toDateRef.current?.showPicker?.()}
            className="w-full cursor-pointer rounded-md border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          />
        </div>
        <button
          onClick={handleExportRange}
          className="w-full rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          Export Range
        </button>
      </div>
    </div>
  </div>
)}
          </div>
        </div>

        {/* Empty state for filtered results */}
        {filteredOrders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500 border-b border-gray-100 dark:border-gray-800">
            <FiPackage className="w-12 h-12 mb-4" />
            <p className="text-lg mb-2">No exchange requests found</p>
            <p className="text-sm mb-4">Try adjusting your filters</p>
            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Adjust Filters
            </button>
          </div>
        )}

        <div className="max-w-full overflow-x-auto">
          {/* Desktop Table */}
          <div className="hidden sm:block">
            <Table>
              <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
                <TableRow>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Currency Pair
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Outgoing
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Incoming
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Date
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Status
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Action
                  </TableCell>
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paginatedData.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center -space-x-2">
                          <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                            <img
                              src={order.fromCurrency.flag}
                              className="h-full w-full object-cover"
                              alt={order.fromCurrency.name}
                            />
                          </div>
                          <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                            <img
                              src={order.toCurrency.flag}
                              className="h-full w-full object-cover"
                              alt={order.toCurrency.name}
                            />
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 text-theme-sm dark:text-white/90">
                            {order.fromCurrency.abbr}/{order.toCurrency.abbr}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {order.fromCurrency.symbol}
                      {order.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {showValue(order)}
                    </TableCell>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {formatDate(order.createdAt)}
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge size="sm" color={getOrderStatusBadgeColor(order.status)}>
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="View order details"
                      >
                        View
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Table */}
          <div className="sm:hidden">
            <Table>
              <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
                <TableRow>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Currency
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Outgoing
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Incoming
                  </TableCell>
                  <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                    Action
                  </TableCell>
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paginatedData.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center -space-x-2">
                          <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                            <img
                              src={order.fromCurrency.flag}
                              className="h-full w-full object-cover"
                              alt={order.fromCurrency.name}
                            />
                          </div>
                          <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                            <img
                              src={order.toCurrency.flag}
                              className="h-full w-full object-cover"
                              alt={order.toCurrency.name}
                            />
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {order.fromCurrency.symbol}
                      {order.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {showValue(order)}
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="View order details"
                      >
                        View
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {filteredOrders.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <OrderDetailsModal
        isOpen={isOpen}
        onClose={close}
        order={selectedOrder}
      />
    </>
  );
}
